import React, { useImperativeHandle } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { errorMessage } from 'constants/CommonConstants';
import { EMAIL_REGEX, NAME_REGEX, PHONE_REGEX } from 'constants/Regex';
import { formPatternRules, formRequiredRules } from 'helper';
import { StepperFromProps } from 'sharedComponents/dynamicStepperForm/dynamicStepperFormInterface';
import { CheckboxInput, CommonInput, DateInput } from 'sharedComponents/inputGroup';
import { CommonPhoneInput } from 'sharedComponents/inputGroup/CommonPhoneInput';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';

const Step1: React.ForwardRefRenderFunction<unknown, StepperFromProps> = ({ onNext }, ref) => {
  const { t } = useTranslation();

  const { authenticateClientData } = useSelector((state: RootState) => state.auth);

  const dealershipName = authenticateClientData?.data?.widgetData?.dealershipName;

  const hasCalledNextStep = React.useRef(false);

  // React Hook Form
  const { control, trigger } = useFormContext();

  // React Hook Form Trigger
  const nextStep = () => {
    if (hasCalledNextStep.current) return;
    hasCalledNextStep.current = true;

    onNext?.();
  };

  useImperativeHandle(ref, () => ({
    nextStep,
  }));

  return (
    <div className='csc-screens__form-col shadow'>
      <div className='csc-screens__form-col__top'>
        <div className='csc-screens__form-col__steps'>1/2</div>
        <h3 className='csc-screens__form-col__title'>{t('stepperForm.heading')}</h3>
      </div>
      <div className='csc-screens__form-col__middle'>
        <div className='csc-screens__field-wrapper'>
          <div className='form-group'>
            <CommonInput
              name='first'
              control={control}
              label={t('stepperForm.firstName')}
              placeholder={t('stepperForm.enterFirstName')}
              rules={{
                ...formRequiredRules(errorMessage(t).firstNameRequired),
                ...formPatternRules(NAME_REGEX, errorMessage(t).firstNameInvalid),
              }}
              triggerValidationForField={trigger}
            />
          </div>
          <div className='form-group'>
            <CommonInput
              name='last'
              control={control}
              label={t('stepperForm.lastName')}
              placeholder={t('stepperForm.enterLastName')}
              rules={{
                ...formRequiredRules(errorMessage(t).lastNameRequired),
                ...formPatternRules(NAME_REGEX, errorMessage(t).lastNameInvalid),
              }}
              triggerValidationForField={trigger}
            />
          </div>

          <div className='form-group'>
            <CommonInput
              name='emailAddress'
              control={control}
              label={t('stepperForm.emailEg')}
              hint={t('stepperForm.emailSample')}
              placeholder={t('stepperForm.enterEmail')}
              rules={{
                ...formRequiredRules(errorMessage(t).emailRequired),
                ...formPatternRules(EMAIL_REGEX, errorMessage(t).invalidEmailFormat),
              }}
              triggerValidationForField={trigger}
            />
          </div>
          <div className='form-group'>
            <CommonPhoneInput
              name='phone'
              control={control}
              label={t('stepperForm.phone')}
              placeholder={t('stepperForm.enterPhone')}
              triggerValidationForField={trigger}
              rules={{
                ...formRequiredRules(errorMessage(t).phoneRequired),
                ...formPatternRules(PHONE_REGEX, errorMessage(t).invalidPhoneFormat),
              }}
            />
          </div>
          <div className='form-group datepicker-wrapper'>
            <DateInput
              name='dateValue'
              control={control}
              rules={formRequiredRules(errorMessage(t).dobRequired)}
              label={t('stepperForm.DOB')}
              triggerValidationForField={trigger}
              maxDate={new Date(new Date().setFullYear(new Date().getFullYear() - 18))}
            />
          </div>

          <div className='form-group'>
            <CheckboxInput
              name='termsAndCondition'
              control={control}
              label={t('stepperForm.note', { dealershipName })}
              rules={formRequiredRules(errorMessage(t).termsAndConditionRequired)}
              triggerValidationForField={trigger}
              className='align-top'
            />
          </div>

          <div className='form-group'>
            <CheckboxInput
              name='termsAndConditionConsent'
              control={control}
              label={t('stepperFormCredit.termsConditions')}
              rules={formRequiredRules(errorMessage(t).required)}
              triggerValidationForField={trigger}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const StepperForm1 = React.forwardRef(Step1);

export default StepperForm1;
